import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Sort, Search, Toolbar, VirtualScroll } from '@syncfusion/ej2-react-grids';

import useFetchIntervenants from '../hooks/useFetchIntervenants';

import AddPopup from '../components/AddPopup';

import SearchBar from '../utils/SearchBar';

const ListIntervenant = ({ lstIntervenants, uuidDeclaration = null, columns = [], showAddButton = true, size = 520, setSelectedRow = null, handleRowDoubleClick = null, loading = false, refreshIntervenants = null }) => {

    const [popupMode, setPopupMode] = useState("add");

    const gridRef = useRef();

    if (columns.length === 0) {
        columns = [
            { field: "Nom", headerText: "Société", width: "300px" },
            { field: "_Type", headerText: "Type", width: "180px", },
            { field: "Activite", headerText: "Activité", width: "250px" },
            { field: "Ville", headerText: "Ville", width: "180px" },
            { field: "Adresse", headerText: "Adresse", width: "230px" },
            { field: "CP", headerText: "CP", width: "100px" },
            { field: "Mail", headerText: "Em@il", width: "230px" },
            { field: "Tel", headerText: "Telephone", width: "180px" },
            { field: "Forme_juridique", headerText: "Forme Juridique", width: "200px" },
            { field: "Siret", headerText: "N° Siret", width: "180px" },
            { field: "_Date_Crea", headerText: "Créé le", type: "date", format: "dd/MM/yyyy", width: "130px" },
        ];
    }

    const { intervenants, fetchIntervenants, fetchAddIntervenant, fetchAddIntervenantToDeclaration } = useFetchIntervenants();

    const [intervenantsPotentiels, setIntervenantsPotentiels] = useState([]);

    const [filtersVisible, setFiltersVisible] = useState(false);
    const [query, setQuery] = useState("");

    const norm = (s) =>
        (s ?? "")
            .toString()
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "");

    const SEARCH_FIELDS = ['Nom', 'Type', 'Forme_juridique', 'Activité', 'CP', 'Mail', 'Tel', 'Siret'];

    const [filteredData, setFilteredData] = useState([]);

    useEffect(() => {
        let filtered = lstIntervenants;

        if (query.trim()) {
            const q = norm(query);
            filtered = filtered.filter((d) =>
                SEARCH_FIELDS.some((f) => norm(d?.[f]).includes(q))
            );
        }

        setFilteredData(filtered);
    }, [query, lstIntervenants]);


    useEffect(() => {
        if (uuidDeclaration) {
            fetchIntervenants();
        }
    }, [uuidDeclaration]);

    useEffect(() => {

        const intervenantsPotentiels = intervenants?.filter((interv) => {
            return !lstIntervenants.some((linked) => linked.UUID_ === interv.UUID_);
        }) || [];

        setIntervenantsPotentiels(intervenantsPotentiels);

    }, [intervenants, lstIntervenants]);

    const [showPopup, setShowPopup] = useState(false);
    const [newIntervenant, setNewIntervenant] = useState({
        Nom: '',
        Activite: '',
        Adresse: '',
        CP: '',
        Ville: '',
        Forme_juridique: '',
        Siret: '',
        Tel: '',
        Mail: '',
        _Type: ''
    });

    const fields = [
        { name: 'Nom', label: 'Société', type: 'text', required: true, },
        { name: '_Type', label: 'Type', type: 'text', required: true, type: "select", options: ["Entreprise Générale", "Maître d'Oeuvre", "Maître d'Ouvrage", "BET", "Controleur Technique", "Fabricant", "Co-Traitant", "Sous-Traitant", "Autre"] },
        { name: 'Activite', label: 'Activité', type: 'text', required: false, },
        { name: 'Adresse', label: 'Adresse', type: 'text', required: true, },
        { name: 'CP', label: 'Code Postal', type: 'text', required: false, },
        { name: 'Ville', label: 'Ville', type: 'text', required: false, },
        { name: 'Forme_juridique', label: 'Forme Juridique', type: 'text', required: false, },
        { name: 'Siret', label: 'N° Siret', type: 'text', required: true, },
        { name: 'Tel', label: 'Téléphone', type: 'text', required: false, },
        { name: 'Mail', label: 'E-mail', type: 'email', required: true, },
    ]

    const handleOpenPopup = () => {
        setShowPopup(true);
    };

    const handleClosePopup = () => {
        setShowPopup(false);
        setNewIntervenant({
            Nom: '',
            Activite: '',
            Adresse: '',
            CP: '',
            Ville: '',
            Forme_juridique: '',
            Siret: '',
            Tel: '',
            Mail: '',
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setNewIntervenant((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const emptyMessageTemplate = useCallback(() => {
        if (loading) {
            return (
                <div>
                    {Array.from({ length: 15 }).map((_, i) => (
                        <div key={i} className="flex flex-row gap-4 p-3 border-b-2 text-gray-500">
                            {Array.from({ length: 5 }).map((_, j) => (
                                <div key={j} className="bg-gray-200 rounded-md h-4 animate-pulse" style={{ width: `${Math.random() * 30 + 10}rem` }}></div>
                            ))}
                        </div>
                    ))}
                </div>
            );
        }
        return <div className="p-4 text-gray-500 text-start">Aucun résultat trouvé.</div>;
    }, [loading]);

    const handleAddIntervenant = async () => {
        if (popupMode === "add") {
            const intervenantToAdd = { ...newIntervenant };
            await fetchAddIntervenant(intervenantToAdd);
            lstIntervenants.push(intervenantToAdd);
            handleClosePopup();
        } else if (popupMode === "link") {

            console.log("Linking Intervenant to Declaration", newIntervenant);
            if (!uuidDeclaration || !newIntervenant?.UUID_) {
                return;
            }

            try {
                await fetchAddIntervenantToDeclaration(uuidDeclaration, newIntervenant.UUID_);
                if (refreshIntervenants !== null && typeof refreshIntervenants === 'function') {
                    refreshIntervenants();
                }
                handleClosePopup();
            } catch (error) {
                console.error(error);
            }
        }
    };

    useEffect(() => {
        if (gridRef.current) {
            gridRef.current.dataSource = filteredData;
            gridRef.current.dataBind();
        }
    }, [filteredData]);

    return (
        <div className="bg-white p-4 rounded-lg">
            <div className="flex lg:flex-row flex-col justify-between items-start lg:items-center mx-auto my-4 lg:my-8 px-6 pb-4 border-gray-300 border-b container">
                <div>
                    <h4 className="font-bold text-gray-800 text-2xl leading-tight">Intervenants</h4>
                    <ul className="flex md:flex-row flex-col items-start md:items-center mt-3 text-gray-600 text-sm">
                        <li className="flex items-center mt-3 md:mt-0 mr-3">
                            <span className="mr-2">
                                <i className="fa-user fas"></i>
                            </span>
                            <div className="flex items-center gap-1">
                                {loading ? (
                                    <div className="bg-gray-200 rounded-md w-9 h-4 animate-pulse"></div>
                                ) : (
                                    <span>{lstIntervenants.length}</span>
                                )}
                                {' '}{lstIntervenants.length === 1 ? 'Intervenant' : 'Intervenants'}
                            </div>
                        </li>
                    </ul>
                </div>
                {showAddButton && (
                    <div className="mt-6 lg:mt-0">
                        <button
                            onClick={() => {
                                setPopupMode("add");
                                handleOpenPopup();
                            }}
                            className="bg-orange-600 hover:bg-orange-500 px-8 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 text-white text-sm transition duration-300 ease-in-out"
                        >
                            Ajouter un Intervenant
                        </button>
                    </div>
                )}
                {uuidDeclaration && (
                    <div className="mt-6 lg:mt-0">
                        <button
                            onClick={() => {
                                setPopupMode("link");
                                handleOpenPopup();
                            }}
                            className="bg-orange-600 hover:bg-orange-500 px-8 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-orange-700 focus:ring-offset-2 text-white text-sm transition duration-300 ease-in-out"
                        >
                            Lier un Intervenant à la Déclaration
                        </button>
                    </div>
                )}
            </div>

            <div className="lg:mx-auto mb-4 lg:mb-8 px-6 container">
                <div className="flex justify-between mb-4">
                    <div></div>
                    {/* <button
                        onClick={() => setFiltersVisible(!filtersVisible)}
                        className="flex items-center text-gray-500 hover:text-orange-600 text-sm"
                    >
                        {filtersVisible ? (
                            <i className="fa-chevron-up fas"></i>
                        ) : (
                            <i className="fa-chevron-down fas"></i>
                        )}
                        <span className="ml-2">{filtersVisible ? 'Réduire' : 'Afficher'} les filtres</span>
                    </button> */}
                    <div className="w-[20rem]">
                        <SearchBar
                            value={query}
                            onChange={setQuery}
                            onClear={() => setQuery("")}
                            placeholder="Rechercher …"
                            disabled={loading}
                        />
                    </div>
                </div>
                <div className="border-2 border-gray-300 hover:border-orange-400 rounded w-full transition duration-300 ease-in-out grow">
                    <GridComponent
                        ref={gridRef}
                        dataSource={filteredData}
                        allowSorting={true}
                        emptyRecordTemplate={emptyMessageTemplate}
                        recordDoubleClick={handleRowDoubleClick ? handleRowDoubleClick : null}
                        enableStickyHeader={true}
                        enableVirtualization={true}
                        height={size}
                        loadingIndicator={{ indicatorType: 'Shimmer' }}
                        sortSettings={{
                            columns: [{ field: 'Nom', direction: 'Ascending' }]
                        }}
                        rowSelected={(args) => {
                            if (setSelectedRow) {
                                lstIntervenants.forEach((item) => {
                                    if (item.UUID_ === args.data.UUID_) {
                                        setSelectedRow(item);
                                    }
                                });
                            }
                        }}
                    >
                        <ColumnsDirective>
                            {columns.map(col => (
                                <ColumnDirective key={col.field} {...col} />
                            ))}
                        </ColumnsDirective>
                        <Inject services={[Sort, Search, Toolbar, VirtualScroll]} />
                    </GridComponent>
                </div>
            </div>

            <AddPopup
                showPopup={showPopup}
                handleClosePopup={() => setShowPopup(false)}
                handleChange={handleChange}
                handleAddItem={handleAddIntervenant}
                type={"Intervenant" + popupMode}
                fields={fields}
                formData={newIntervenant}
                intervenantsList={popupMode === "link" ? intervenantsPotentiels : []}
            />
        </div>
    );
};

export default ListIntervenant;